import { PITCH_COUNT_RULESETS, getPitcherEligibility } from './pitch-count-rules';

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Get effective OVR (xOVR if available, else base OVR)
// ─────────────────────────────────────────────────────────────────────────────

const getEffectiveOVR = (player: any): number => {
  return player.ovr ?? 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Batting Order Style — single source of truth
//
// Three canonical styles control just two engine levers: how many present
// players are in the batting order, and whether an Extra Hitter (EH) batting-
// only slot exists.
//
//   'Continuous'  — every present player bats (count = roster size). EH slot on.
//   '10 Players'  — 10 bat: 9 fielders + 1 EH. EH slot on.
//   '9 Players'   — 9 bat (the fielders). No EH. `useDH` lets the DH bat in the
//                   pitcher's spot, but does NOT add a slot — still 9 batters.
//
// Legacy values are migrated here so old team docs keep working unchanged:
//   'Everyone Bats'        → 'Continuous'
//   'Traditional 9 Players' + useDH → '10 Players'  (the old 10-batter path)
//   'Traditional 9 Players'         → '9 Players'
// ─────────────────────────────────────────────────────────────────────────────

export function normalizeBattingOrderStyle(settings: any): 'Continuous' | '10 Players' | '9 Players' {
  const style = settings?.battingOrderStyle;
  if (style === 'Continuous' || style === '10 Players' || style === '9 Players') return style;
  // Legacy migration
  if (style === 'Traditional 9 Players') return settings?.useDH ? '10 Players' : '9 Players';
  return 'Continuous'; // covers 'Everyone Bats' and any unset/unknown value
}

/** Whether an Extra Hitter (EH) batting-only slot should be offered. */
export function battingStyleHasEH(settings: any): boolean {
  const style = normalizeBattingOrderStyle(settings);
  return style === 'Continuous' || style === '10 Players';
}

/**
 * How many present players are in the batting order.
 * Continuous → all present; 10 Players → 10; 9 Players → 9.
 * `rosterSize` is the count of present (non-absent) players.
 */
export function battingOrderSize(settings: any, rosterSize: number): number {
  const style = normalizeBattingOrderStyle(settings);
  if (style === '10 Players') return Math.min(10, rosterSize);
  if (style === '9 Players') return Math.min(9, rosterSize);
  return rosterSize; // Continuous
}

// ─────────────────────────────────────────────────────────────────────────────
// Position category helpers
// ─────────────────────────────────────────────────────────────────────────────

export const getCategoryForPos = (pos: string) => {
  if (pos === 'P') return 'pitching';
  if (pos === 'C') return 'catching';
  if (['1B', '2B', '3B', 'SS'].includes(pos)) return 'infield';
  if (['LF', 'LCF', 'CF', 'RCF', 'RF'].includes(pos)) return 'outfield';
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// Eligibility check
// ─────────────────────────────────────────────────────────────────────────────

export const isEligible = (
  player: any,
  pos: string,
  teamSettings: any,
  inningLogs?: Record<number, any[]>,
  currentGameDate?: string,
  currentTournamentId?: string,
  styleOfPlay?: string
) => {
  if (pos === 'BENCH' || pos === 'EH') return true;

  const sortedInningKeys = (logs: Record<number, any[]>): number[] =>
    Object.keys(logs).map(Number).sort((a, b) => a - b);

  // Only enforce Pitcher restrictions for Players Pitch style
  if (pos === 'P' && styleOfPlay === 'playerspitch') {
    // 1. Pitcher Re-entry Logic
    if (teamSettings?.noPitcherReentry && inningLogs) {
      const innings = sortedInningKeys(inningLogs);
      const activeInning = innings.length > 0 ? Math.max(...innings) : 0;
      
      let hasPitchedPreviously = false;
      Object.entries(inningLogs).forEach(([inningNum, log]) => {
        if (Number(inningNum) < activeInning) {
          const playerLog = log.find((l: any) => l.id === player.id);
          if (playerLog?.position === 'P') hasPitchedPreviously = true;
        }
      });

      if (hasPitchedPreviously) {
        const lastInning = activeInning - 1;
        const lastInningLog = inningLogs[lastInning];
        const wasPitchingLastInning =
          lastInningLog?.find((l: any) => l.id === player.id)?.position === 'P';
        if (!wasPitchingLastInning) return false;
      }
    }

    // 2. Pitch Count Eligibility (Mandatory Rest)
    if (teamSettings?.enforcePitchCounts && currentGameDate) {
      const ruleset =
        PITCH_COUNT_RULESETS[teamSettings.pitchCountRuleset || 'little_league'];
      if (ruleset) {
        const ageGroup =
          teamSettings.pitchCountAgeGroup || teamSettings.ageGroup || '12U';
        const eligibility = getPitcherEligibility(
          player.pitchingLog || [],
          ruleset,
          ageGroup,
          new Date(currentGameDate),
          0,
          currentTournamentId,
          true,
          teamSettings.customRules
        );
        if (!eligibility.eligible) return false;
      }
    }
  }

  // 3. Position Rating Check
  return (player.positions?.[pos] ?? 0) > 0;
};

// ─────────────────────────────────────────────────────────────────────────────
// Batting Order Scoring
//
// Used exclusively by the 'Optimized' strategy. All other strategies use their
// own sort logic in sortBattingOrder() below.
//
// Each player receives two scores on a 0–1 scale:
//
//   ON_BASE_SCORE  — how often they reach base.
//     Priority: real OBP from imported stats (most reliable).
//     Fallback:  scouting attributes (contact × 0.5 + eye × 0.4 + aggro × 0.1),
//                normalized from a 1–5 scale to 0–1.
//
//   EXTRA_BASE_SCORE — how much damage they do when they hit.
//     Priority: real SLG from imported stats.
//     Fallback:  scouting attributes (power × 0.7 + contact × 0.3), normalized.
//
//   BATTING_SCORE = (ON_BASE_SCORE × 0.6) + (EXTRA_BASE_SCORE × 0.4)
//     OBP is weighted higher because getting on base compounds — every
//     baserunner amplifies the value of every hitter behind them.
//
//   K_PENALTY — a soft tiebreaker penalty for high strikeout rates.
//     Capped at 0.08 so it never overrides a meaningfully better hitter.
//     Sources: SO/PA from stats, or (1 − contact/5) from scouting as proxy.
// ─────────────────────────────────────────────────────────────────────────────

interface BattingScores {
  onBase: number;       // 0–1
  extraBase: number;    // 0–1
  batting: number;      // 0–1 composite
  kPenalty: number;     // 0–0.08 (subtracted during slot assignment)
}

function computeBattingScores(player: any): BattingScores {
  const stats   = player.stats   || null;
  const hitting = player.attributes?.hitting || {};

  // ── On-Base Score ──────────────────────────────────────────────────────────
  let onBase: number;
  const pa  = stats?.pa  || 0;
  const obp = stats?.obp || 0;

  if (pa >= 3 && obp > 0) {
    // Real stat: OBP already lives on a 0–1 scale; clamp for safety.
    // Confidence: scale in smoothly from 3 PA (30%) to 15 PA (100%).
    const confidence = pa >= 15 ? 1 : 0.30 + ((pa - 3) / 12) * 0.70;
    const scoutOnBase =
      ((hitting.contact || 0) * 0.5 +
       (hitting.eye     || 0) * 0.4 +
       (hitting.approach   || 0) * 0.1) / 5;
    onBase = confidence * Math.min(obp, 1) + (1 - confidence) * scoutOnBase;
  } else {
    // Pure scouting fallback
    onBase =
      ((hitting.contact || 0) * 0.5 +
       (hitting.eye     || 0) * 0.4 +
       (hitting.approach   || 0) * 0.1) / 5;
  }

  // ── Extra-Base Score ───────────────────────────────────────────────────────
  let extraBase: number;
  const slg = stats?.slg || 0;

  if (pa >= 3 && slg > 0) {
    // Real stat: SLG can exceed 1.0 (e.g. all HRs = 4.0); cap at 1.0 for scoring.
    const confidence = pa >= 15 ? 1 : 0.30 + ((pa - 3) / 12) * 0.70;
    const scoutExtraBase =
      ((hitting.power   || 0) * 0.7 +
       (hitting.contact || 0) * 0.3) / 5;
    extraBase =
      confidence * Math.min(slg, 1) + (1 - confidence) * scoutExtraBase;
  } else {
    extraBase =
      ((hitting.power   || 0) * 0.7 +
       (hitting.contact || 0) * 0.3) / 5;
  }

  // ── Composite Batting Score ────────────────────────────────────────────────
  const batting = onBase * 0.6 + extraBase * 0.4;

  // ── K-Rate Penalty ────────────────────────────────────────────────────────
  // Soft tiebreaker only — max 0.08 so it cannot override a meaningfully
  // better hitter. Prevents clustering high-K batters at the top of the order.
  let kPenalty = 0;
  if (pa >= 3 && stats?.so !== undefined) {
    const kPct = stats.so / pa;              // 0–1
    // penalty scales 0 → 0.08 between 20% K and 70% K
    kPenalty = Math.min(0.08, Math.max(0, ((kPct - 0.20) / 0.50) * 0.08));
  } else {
    // Scouting proxy: low contact → higher K tendency
    const contactRating = (hitting.contact || 0) / 5; // 0–1
    kPenalty = Math.min(0.08, Math.max(0, (1 - contactRating) * 0.06));
  }

  return { onBase, extraBase, batting, kPenalty };
}

// ─────────────────────────────────────────────────────────────────────────────
// Optimized Batting Order Sort
//
// Slot assignment follows modern MLB lineup construction theory: put your best
// overall hitters at the top of the order (spots 1–2) to maximise their plate
// appearances over a short youth game, then cluster power through the middle,
// and place disciplined on-base guys in the 6–7 slots to reset the order.
//
//   Spots 1–2  — highest BATTING_SCORE (best all-around hitters get most PAs)
//   Spot  3    — highest EXTRA_BASE_SCORE among remaining (power after top two)
//   Spots 4–5  — next highest BATTING_SCORE (heart of the order cluster)
//   Spots 6–7  — highest ON_BASE_SCORE among remaining (second leadoff type)
//   Spots 8–N  — remaining, descending BATTING_SCORE
//
// Within each slot group, the K_PENALTY breaks ties between similar players.
// ─────────────────────────────────────────────────────────────────────────────

function sortOptimized(players: any[]): any[] {
  // players passed in are already exclusively active players (no BENCH, EH, ABSENT)
  
  // Pre-compute scores for every active player once.
  const scored = players.map(p => ({ player: p, scores: computeBattingScores(p) }));

  const n = scored.length;
  const assigned: any[] = new Array(n).fill(null);
  let pool = [...scored];

  // Helper: pull the best from pool by a comparator, removing from pool.
  const pick = (
    compareFn: (a: typeof scored[0], b: typeof scored[0]) => number
  ) => {
    if (pool.length === 0) return null;
    pool.sort(compareFn);
    const best = pool[0];
    pool = pool.slice(1);
    return best;
  };

  // Effective score with K penalty applied (used for all batting-score sorts).
  const effectiveBatting = (s: typeof scored[0]) =>
    s.scores.batting - s.scores.kPenalty;

  // ── Slot 1: Best overall hitter ───────────────────────────────────────────
  if (n >= 1) {
    const p = pick((a, b) => effectiveBatting(b) - effectiveBatting(a));
    if (p) assigned[0] = p.player;
  }

  // ── Slot 2: Second-best overall hitter ───────────────────────────────────
  if (n >= 2) {
    const p = pick((a, b) => effectiveBatting(b) - effectiveBatting(a));
    if (p) assigned[1] = p.player;
  }

  // ── Slot 3: Best extra-base / power bat among remaining ──────────────────
  if (n >= 3) {
    const p = pick(
      (a, b) =>
        b.scores.extraBase - a.scores.extraBase ||
        effectiveBatting(b) - effectiveBatting(a)
    );
    if (p) assigned[2] = p.player;
  }

  // ── Slots 4–5: Next best batting scores (heart of the order) ─────────────
  for (let i = 3; i <= 4 && i < n; i++) {
    const p = pick((a, b) => effectiveBatting(b) - effectiveBatting(a));
    if (p) assigned[i] = p.player;
  }

  // ── Slots 6–7: Best on-base guys remaining (second-leadoff reset) ─────────
  for (let i = 5; i <= 6 && i < n; i++) {
    const p = pick(
      (a, b) =>
        b.scores.onBase - a.scores.onBase ||
        effectiveBatting(b) - effectiveBatting(a)
    );
    if (p) assigned[i] = p.player;
  }

  // ── Slots 8–N: Remaining players, best batting score first ───────────────
  let slot = 7;
  while (pool.length > 0 && slot < n) {
    const p = pick((a, b) => effectiveBatting(b) - effectiveBatting(a));
    if (p) assigned[slot] = p.player;
    slot++;
  }

  // Collect any still-unassigned (shouldn't happen, but safety net).
  const unplaced = pool.map(s => s.player);

  return [...assigned.filter(Boolean), ...unplaced];
}

// ─────────────────────────────────────────────────────────────────────────────
// sortBattingOrder
//
// Applies the coach-selected batting strategy to a lineup that has already had
// defensive positions assigned by generateOptimalLineup().
//
// This is the single exit point for all batting-order logic. Call it in
// App.tsx (or wherever autoGenerate runs) instead of inline strategy code.
//
// Strategies (canonical keys — see ORDER_MODES in batting-order.ts):
//   'None'                  — preserve the order returned by generateOptimalLineup
//   'Fairness'              — fewest season plate appearances bats first
//   'Minimize Strikeouts'   — front-load the hardest-to-strike-out bats
//   'Optimized'             — modern MLB construction (best hitters get most PAs)
//   'Distribute Weaker Hitters' — interleave strong and weak throughout the order
// Legacy aliases migrated at the top of the function: 'Equal ABs'→'Fairness',
// 'Power'→'Optimized'.
// ─────────────────────────────────────────────────────────────────────────────

export function sortBattingOrder(lineup: any[], strategy: string): any[] {
  // Migrate legacy stored strategy names to the engine's canonical keys. Older
  // teams may have 'Equal ABs' or 'Power' saved (Settings offered them but the
  // engine never had matching cases, so they silently no-op'd). Normalize here
  // — the single engine entry point — so stored legacy values work immediately,
  // without waiting for a Settings re-save.
  if (strategy === 'Equal ABs') strategy = 'Fairness';
  else if (strategy === 'Power') strategy = 'Optimized';

  if (!strategy || strategy === 'None') return lineup;

  // Batting order is independent of defensive position. Every player passed
  // here is a batter — the caller handles absent/non-batting exclusions.
  let ordered: any[];

  switch (strategy) {

    // ── Fairness ──────────────────────────────────────────────────────────────
    case 'Fairness': {
      ordered = [...lineup].sort((a, b) => {
        // 1. Fewest season plate appearances leads off
        const paDiff = (a.stats?.pa || 0) - (b.stats?.pa || 0);
        if (paDiff !== 0) return paDiff;
        // 2. Tiebreak: more bench innings this season → deserves more at-bats
        const benchDiff = (b.seasonBenchInnings || 0) - (a.seasonBenchInnings || 0);
        if (benchDiff !== 0) return benchDiff;
        // 3. Final tiebreak: lower OVR leads off (weaker hitters benefit most from PAs)
        return getEffectiveOVR(a) - getEffectiveOVR(b);
      });
      break;
    }

    // ── Minimize Strikeouts ───────────────────────────────────────────────────
    // Front-load the hardest-to-strikeout bats. In a 6-inning youth game,
    // avoiding consecutive Ks matters more than raw power.
    // Stat: K% (so / pa) ascending when PA≥3. Scouting fallback: contact rating.
    case 'Minimize Strikeouts': {
      ordered = [...lineup].sort((a, b) => {
        const aPa = a.stats?.pa || 0;
        const bPa = b.stats?.pa || 0;
        const aK = aPa >= 3 ? (a.stats?.so || 0) / aPa : null;
        const bK = bPa >= 3 ? (b.stats?.so || 0) / bPa : null;

        // Both have stats — sort by K% ascending (fewer Ks = better)
        if (aK !== null && bK !== null) return aK - bK;
        // Only one has stats — stat-backed player first
        if (aK !== null) return -1;
        if (bK !== null) return 1;
        // Neither has stats — scouting fallback: higher contact rating first
        return (b.attributes?.hitting?.contact || 0) - (a.attributes?.hitting?.contact || 0);
      });
      break;
    }

    // ── OPS First ─────────────────────────────────────────────────────────────
    // The modern MLB construction: the best overall bat leads off so he gets the
    // most trips to the plate. Over six youth innings that is worth roughly one
    // extra at-bat versus hitting him third.
    // Stat: season OPS descending when PA>=3. Scouting fallback: contact + power.
    case 'OPS First': {
      ordered = [...lineup].sort((a, b) => {
        const aOps = (a.stats?.pa || 0) >= 3 ? (a.stats?.ops || 0) : null;
        const bOps = (b.stats?.pa || 0) >= 3 ? (b.stats?.ops || 0) : null;

        // Both have a real sample: straight OPS, best first.
        if (aOps !== null && bOps !== null) return bOps - aOps;
        // Only one has a sample: the stat-backed player leads.
        if (aOps !== null) return -1;
        if (bOps !== null) return 1;
        // Neither has a sample: fall back to the scouting grades.
        const scouted = (p: any) =>
          (p.attributes?.hitting?.contact || 0) + (p.attributes?.hitting?.power || 0);
        return scouted(b) - scouted(a);
      });
      break;
    }

    // ── Speed Spread ──────────────────────────────────────────────────────────
    // Keeps a baserunning threat in front of every part of the order instead of
    // stacking the fast kids at the top. Interleaves the faster half with the
    // slower half, the same shape as Distribute Weaker Hitters.
    // Stat: stolen bases per game once there is a sample, plus the speed grade.
    case 'Speed Spread': {
      const speedScore = (p: any) => {
        const gp = p.stats?.gp || 0;
        // Scouting speed is 1-5; normalised to 0-1 so it sits on the same scale
        // as a per-game steal rate rather than swamping it.
        const scouted = (p.attributes?.intangibles?.speed || 0) / 5;
        return gp >= 2 ? (p.stats?.sb || 0) / gp + scouted : scouted;
      };

      const scored = [...lineup]
        .map(p => ({ player: p, score: speedScore(p) }))
        .sort((a, b) => b.score - a.score);

      const half = Math.ceil(scored.length / 2);
      const fast = scored.slice(0, half);
      const slow = scored.slice(half);

      ordered = [];
      for (let i = 0; i < fast.length; i++) {
        ordered.push(fast[i].player);
        if (slow[i]) ordered.push(slow[i].player);
      }
      break;
    }

    // ── Optimized ─────────────────────────────────────────────────────────────
    case 'Optimized': {
      ordered = sortOptimized(lineup);
      break;
    }

    // ── Distribute Weaker Hitters ─────────────────────────────────────────────
    // Interleave strong and weak bats so no consecutive weak spots in the order.
    // Stat: OPS-proxy (batting - kPenalty from computeBattingScores). Falls back
    // to scouting automatically when PA < 3.
    case 'Distribute Weaker Hitters': {
      const scored = [...lineup].map(p => ({
        player: p,
        score: computeBattingScores(p),
      })).sort((a, b) =>
        (b.score.batting - b.score.kPenalty) - (a.score.batting - a.score.kPenalty)
      );

      const half   = Math.ceil(scored.length / 2);
      const strong = scored.slice(0, half);
      const weak   = scored.slice(half);

      ordered = [];
      for (let i = 0; i < strong.length; i++) {
        ordered.push(strong[i].player);
        if (weak[i]) ordered.push(weak[i].player);
      }
      break;
    }

    default:
      ordered = lineup;
  }

  return ordered;
}

// ─────────────────────────────────────────────────────────────────────────────
// generateOptimalLineup
//
// Assigns defensive positions to all players using the Scarcity Model, applies
// Field Time Equalizer rules, and returns the positioned lineup + any warnings.
//
// Batting order is NOT applied here — call sortBattingOrder() on the result
// in App.tsx after this function returns, passing settings.battingStrategy.
// ─────────────────────────────────────────────────────────────────────────────

export const generateOptimalLineup = (
  players: any[],
  teamSettings: any,
  inningLogs?: Record<number, any[]>,
  currentGameDate?: string,
  currentTournamentId?: string,
  styleOfPlay?: string,
  // Coach's explicit "these players start on the bench" picks (from the
  // Tournament Bench Plan). Additive: when empty/undefined the engine behaves
  // exactly as before. These ids are placed on the bench first; fairness fills
  // any remaining bench slots. Catcher/pitcher protection still applies after.
  forceBenchIds?: string[]
) => {
  const settings = teamSettings || {};
  const isPlayersPitch = styleOfPlay === 'playerspitch';

  // ── Field Time Equalizer toggle ──────────────────────────────────────────
  const fieldTimeOn: boolean =
    settings.fieldTimeEqualizerEnabled ||
    settings.fieldTimeEnabled ||       // legacy
    settings.fairnessEngineEnabled ||  // legacy orphan
    false;

  // ── Inning log helpers ───────────────────────────────────────────────────
  const sortedInningKeys = (logs: Record<number, any[]>): number[] =>
    Object.keys(logs).map(Number).sort((a, b) => a - b);

  const getInningsPlayed = (
    playerId: string,
    logs: Record<number, any[]>
  ): number => {
    let count = 0;
    sortedInningKeys(logs).forEach(k => {
      const entry = logs[k].find((p: any) => p.id === playerId);
      if (entry && entry.position !== 'BENCH' && entry.position !== 'EH') count++;
    });
    return count;
  };

  const getBenchInnings = (
    playerId: string,
    logs: Record<number, any[]>
  ): number => {
    let count = 0;
    sortedInningKeys(logs).forEach(k => {
      const entry = logs[k].find((p: any) => p.id === playerId);
      if (entry && (entry.position === 'BENCH' || entry.position === 'EH')) count++;
    });
    return count;
  };

  const getLastPosition = (
    playerId: string,
    logs: Record<number, any[]>
  ): string | null => {
    const keys = sortedInningKeys(logs);
    if (keys.length === 0) return null;
    const lastInning = logs[keys[keys.length - 1]];
    return lastInning?.find((p: any) => p.id === playerId)?.position ?? null;
  };

  // Returns the most recent inning number in which this player was on the bench,
  // or -1 if they have never been benched in the current game.
  // Used as a tiebreaker: player who last sat EARLIEST is most "due" for their next turn.
  const getLastBenchInning = (
    playerId: string,
    logs: Record<number, any[]>
  ): number => {
    const keys = sortedInningKeys(logs);
    for (let i = keys.length - 1; i >= 0; i--) {
      const entry = logs[keys[i]].find((p: any) => p.id === playerId);
      if (entry && (entry.position === 'BENCH' || entry.position === 'EH')) return keys[i];
    }
    return -1; // never benched this game — most overdue, sits first among equals
  };

  // ── Field positions ──────────────────────────────────────────────────────
  const fieldPositions = [
    ...(settings.noPitcher  ? [] : ['P']),
    ...(settings.noCatcher  ? [] : ['C']),
    '1B', '2B', '3B', 'SS', 'LF',
    ...(settings.fourOutfielders ? ['LCF', 'RCF'] : ['CF']),
    'RF',
  ];

  // ── Enrich players with game-state bench tracking ────────────────────────
  let enrichedPlayers = players.map((p: any) => {
    let satTotal        = 0;
    let consecutiveBench = 0;
    let lastPosition: string | null = null;
    let positionsPlayed: Record<string, number> = {};

    if (inningLogs) {
      sortedInningKeys(inningLogs).forEach(k => {
        const playerLog = inningLogs[k].find((l: any) => l.id === p.id);
        if (playerLog) {
          lastPosition = playerLog.position;
          if (playerLog.position === 'BENCH' || playerLog.position === 'EH') {
            satTotal++;
            consecutiveBench++;
          } else {
            consecutiveBench = 0;
            positionsPlayed[playerLog.position] =
              (positionsPlayed[playerLog.position] || 0) + 1;
          }
        }
      });
    }

    const seasonBenchTotal: number =
      typeof p.seasonBenchInnings === 'number'
        ? p.seasonBenchInnings + satTotal
        : satTotal;

    return {
      ...p,
      satTotal,
      seasonBenchTotal,
      consecutiveBench,
      lastPosition,
      positionsPlayed,
    };
  });

  const numBench = Math.max(0, enrichedPlayers.length - fieldPositions.length);

  // ── Bench selection sort ─────────────────────────────────────────────────
  // Goal: equal bench time across the season, with no skill bias.
  //
  // 1. seasonBenchTotal (ascending) — player who has sat least this season sits next.
  // 2. getLastBenchInning (ascending) — among players tied on bench count, the one
  //    who sat EARLIEST is most overdue for their next turn. This creates a true
  //    rotation cycle rather than always picking the same (low-OVR) players.
  //    Returns -1 for players who haven't sat yet this game, so they sort first. ✓
  // 3. Alphabetical name — deterministic, no skill bias, used when logs are empty.
  //
  // OVR is intentionally NOT used here — weaker players must not bear extra bench burden.
  // Ordering of who sits NEXT, ignoring the no-repeat guard (that's a hard
  // partition handled below). Pure fairness: least season bench first, then most
  // overdue, then alphabetical. OVR is never used — weaker players must not bear
  // extra bench burden.
  const benchOrder = (a: any, b: any): number => {
    if (fieldTimeOn) {
      if (a.seasonBenchTotal !== b.seasonBenchTotal)
        return a.seasonBenchTotal - b.seasonBenchTotal;
      if (inningLogs && Object.keys(inningLogs).length > 0) {
        const aLast = getLastBenchInning(a.id, inningLogs);
        const bLast = getLastBenchInning(b.id, inningLogs);
        if (aLast !== bLast) return aLast - bLast;
      }
      return (a.name || '').localeCompare(b.name || '');
    }
    return getEffectiveOVR(a) - getEffectiveOVR(b);
  };

  // ── No Repeat Bench: HARD guard (not a tiebreaker) ───────────────────────────
  // A player who sat last inning is held OUT of this inning's bench pool entirely,
  // as long as enough other players exist to fill the bench. Only if the bench
  // can't be filled without them do we let just-sat players sit again (fewest
  // alternatives first). This makes a stale/imbalanced season bench total unable
  // to pin a kid to the bench two innings running.
  //
  // KNOWN GAP: the no-repeat guard lives here and in the Equal Bench enforcement
  // pass below, but the Catcher-protection swap (and potentially Must-Play-Infield)
  // can still re-bench a just-sat player. Fully closing this wants a single final
  // guard pass after all enforcement stages — tracked for a follow-up. Until then,
  // the Ready-time Fairness re-check (Checkpoint B) alerts the coach on any leak.
  const noRepeat =
    settings.noConsecutiveBench && inningLogs && Object.keys(inningLogs).length > 0;

  // ── Coach's explicit bench picks seed the bench first ────────────────────────
  // When the coach has chosen who sits (Tournament Bench Plan), honor those picks
  // before automatic fairness fills any leftover bench slots. Capped at numBench.
  const forcedSet = new Set(forceBenchIds || []);
  const forcedBench = forcedSet.size > 0
    ? enrichedPlayers.filter(p => forcedSet.has(p.id)).slice(0, numBench)
    : [];
  const remainingBenchSlots = Math.max(0, numBench - forcedBench.length);
  const poolAfterForced = enrichedPlayers.filter(p => !forcedSet.has(p.id));

  let benchedThisInning: any[];
  if (noRepeat) {
    const justSat   = poolAfterForced.filter(p => p.consecutiveBench > 0).sort(benchOrder);
    const available = poolAfterForced.filter(p => p.consecutiveBench === 0).sort(benchOrder);
    // Bench from players who did NOT just sit. Top up from just-sat only if forced.
    let autoBench = available.slice(0, remainingBenchSlots);
    if (autoBench.length < remainingBenchSlots) {
      autoBench = autoBench.concat(
        justSat.slice(0, remainingBenchSlots - autoBench.length)
      );
    }
    benchedThisInning = [...forcedBench, ...autoBench];
  } else {
    benchedThisInning = [
      ...forcedBench,
      ...[...poolAfterForced].sort(benchOrder).slice(0, remainingBenchSlots),
    ];
  }

  let fieldingPlayers = enrichedPlayers.filter(
    p => !benchedThisInning.find(bp => bp.id === p.id)
  );

  // ── Catcher protection (hard rule) ───────────────────────────────────────
  // Never bench the best available catcher when a worse/non-catcher is on the field.
  // Field Time fairness cannot override this.
  if (!settings.noCatcher) {
    const cRating = (p: any) => p.positions?.['C'] || 0;
    const bestFieldingC = [...fieldingPlayers]
      .filter(p => cRating(p) > 0)
      .sort((a, b) => cRating(b) - cRating(a))[0];
    const bestBenchedC  = [...benchedThisInning]
      .filter(p => cRating(p) > 0)
      .sort((a, b) => cRating(b) - cRating(a))[0];

    if (bestBenchedC && (!bestFieldingC || cRating(bestBenchedC) > cRating(bestFieldingC))) {
      const demote = [...fieldingPlayers]
        .filter(p => !p.isCurrentPitcher)
        .sort((a, b) => getEffectiveOVR(a) - getEffectiveOVR(b))[0];
      if (demote) {
        benchedThisInning = benchedThisInning.filter(p => p.id !== bestBenchedC.id);
        benchedThisInning.push(demote);
        fieldingPlayers = fieldingPlayers.filter(p => p.id !== demote.id);
        fieldingPlayers.push(bestBenchedC);
      }
    }
  }

  // ── Pitcher protection (hard rule, Players Pitch only) ───────────────────
  // Mirrors catcher protection. Bench selection runs before positions are
  // assigned and is blind to pitching, so with no starter named it could bench
  // every rated arm and leave nobody to take the mound. Never let the best
  // available arm sit while no rated pitcher is on the field.
  if (isPlayersPitch && !settings.noPitcher) {
    const pRating = (p: any) => p.positions?.['P'] || 0;
    const anyArmFielding = fieldingPlayers.some(p => pRating(p) > 0);
    const bestBenchedArm = [...benchedThisInning]
      .filter(p => pRating(p) > 0)
      .sort((a, b) => pRating(b) - pRating(a) || getEffectiveOVR(b) - getEffectiveOVR(a))[0];

    if (bestBenchedArm && !anyArmFielding) {
      // Sit the weakest fielder who is not the coach's locked pitcher.
      const demote = [...fieldingPlayers]
        .filter(p => !p.isCurrentPitcher)
        .sort((a, b) => getEffectiveOVR(a) - getEffectiveOVR(b))[0];
      if (demote) {
        benchedThisInning = benchedThisInning.filter(p => p.id !== bestBenchedArm.id);
        benchedThisInning.push(demote);
        fieldingPlayers = fieldingPlayers.filter(p => p.id !== demote.id);
        fieldingPlayers.push(bestBenchedArm);
      }
    }
  }

  // ── Positional coverage protection ──────────────────────────────────────
  {
    const eligibleCount = (pos: string, pool: typeof fieldingPlayers) =>
      pool.filter(p =>
        isEligible(p, pos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
      ).length;

    const positionsByScarcity = [...fieldPositions].sort(
      (a, b) => eligibleCount(a, fieldingPlayers) - eligibleCount(b, fieldingPlayers)
    );

    for (const pos of positionsByScarcity) {
      if (eligibleCount(pos, fieldingPlayers) > 0) continue;

      const promoted = [...benchedThisInning]
        .filter(p =>
          isEligible(p, pos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
        )
        .sort((a, b) =>
          (b.positions?.[pos] || 0) - (a.positions?.[pos] || 0) ||
          getEffectiveOVR(b) - getEffectiveOVR(a)
        )[0];

      if (!promoted) continue;

      const demoted = [...fieldingPlayers]
        .filter(p => !p.isCurrentPitcher)
        .sort((a, b) => getEffectiveOVR(a) - getEffectiveOVR(b))
        .find(candidate =>
          fieldPositions.every(otherPos => {
            if (otherPos === pos) return true;
            const remainingCoverage = fieldingPlayers.some(
              fp =>
                fp.id !== candidate.id &&
                isEligible(fp, otherPos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
            );
            const promotedCovers = isEligible(
              promoted, otherPos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay
            );
            return remainingCoverage || promotedCovers;
          })
        );

      if (!demoted) {
        // No clean swap found — force-promote the eligible player anyway,
        // demoting whoever has the lowest OVR. A coverage gap elsewhere is
        // always better than leaving a scarce position (e.g. C, SS) unfilled.
        const forceDemote = [...fieldingPlayers]
          .filter(p => !p.isCurrentPitcher)
          .sort((a, b) => getEffectiveOVR(a) - getEffectiveOVR(b))[0];
        if (!forceDemote) continue;
        benchedThisInning = benchedThisInning.filter(p => p.id !== promoted.id);
        benchedThisInning.push(forceDemote);
        fieldingPlayers = fieldingPlayers.filter(p => p.id !== forceDemote.id);
        fieldingPlayers.push(promoted);
        continue;
      }

      benchedThisInning = benchedThisInning.filter(p => p.id !== promoted.id);
      benchedThisInning.push(demoted);
      fieldingPlayers = fieldingPlayers.filter(p => p.id !== demoted.id);
      fieldingPlayers.push(promoted);
    }
  }

  let newLineup: any[] = [];
  benchedThisInning.forEach(p => {
    newLineup.push({ ...players.find(l => l.id === p.id), position: 'BENCH' });
  });

  // ── Scarcity Model (position assignment) ─────────────────────────────────
  let unassignedPlayers = [...fieldingPlayers];

  let sortedAvailablePos = [...fieldPositions].sort((a, b) => {
    const aCount = unassignedPlayers.filter(p =>
      isEligible(p, a, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
    ).length;
    const bCount = unassignedPlayers.filter(p =>
      isEligible(p, b, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
    ).length;
    if (aCount !== bCount) return aCount - bCount;
    const priority = ['P', 'C', 'SS', 'CF', '1B', '3B', '2B', 'LCF', 'RCF', 'LF', 'RF'];
    return priority.indexOf(a) - priority.indexOf(b);
  });

  let warnings: string[] = [];
  // Why the mound came up empty, when it did. The Builder uses this to decide
  // whether to ask the coach to pick a starter ('unrated' - nobody on the roster
  // has a P rating yet, which is not an error, just an unanswered question) or to
  // report a genuine blocker ('blocked' - arms exist but rest/re-entry rules sit
  // them all down). null means the mound was filled normally.
  let pitcherGap: null | { reason: 'unrated' | 'blocked' | 'suggested'; message: string } = null;

  // The coach's locked pitcher is spoken for. Positions are filled scarcest-first,
  // so an unrated slot like CF (zero eligible players) is processed BEFORE P and,
  // falling back to "anyone left", would otherwise grab the pitcher. The locked
  // pitcher block at the end then pulls them back to the mound, leaving that slot
  // permanently empty with players still on the bench. Hold them out of every
  // other position's pool so they are only ever assigned to P.
  const lockedPitcherId = isPlayersPitch && !settings.noPitcher
    ? unassignedPlayers.find((p: any) => p.isCurrentPitcher)?.id
    : undefined;

  sortedAvailablePos.forEach(pos => {
    let candidates = [...unassignedPlayers].filter(p =>
      lockedPitcherId
        ? (pos === 'P' ? p.id === lockedPitcherId : p.id !== lockedPitcherId)
        : true
    );
    let eligibleCandidates = candidates.filter(p =>
      isEligible(p, pos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
    );

    if (eligibleCandidates.length === 0 && pos === 'P' && isPlayersPitch) {
      // Split the two very different causes. A player only clears isEligible for
      // 'P' with a rating above zero, so "nobody rated" and "everybody resting"
      // both land here, and they need opposite responses.
      const ratedArms = [...unassignedPlayers].filter(p => (p.positions?.['P'] || 0) > 0);
      const bestBlocked = [...ratedArms]
        .sort((a, b) => (b.positions?.['P'] || 0) - (a.positions?.['P'] || 0))[0];

      if (ratedArms.length === 0) {
        pitcherGap = {
          reason: 'unrated',
          message: 'No one on the roster is rated as a pitcher yet, so Dugout left the mound open for you to fill.',
        };
      } else if (bestBlocked && currentGameDate && settings?.enforcePitchCounts) {
        const log: any[] = bestBlocked.pitchingLog || [];
        const sorted = [...log].sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime());
        const last = sorted[0];
        let message = 'No eligible pitchers remain due to re-entry or rest rules.';
        if (last?.eligibleDate) {
          const gDate = new Date(currentGameDate); gDate.setHours(0, 0, 0, 0);
          const eligible = new Date(last.eligibleDate); eligible.setHours(0, 0, 0, 0);
          if (eligible > gDate) {
            const dateStr = eligible.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
            message = `${bestBlocked.name} is on mandatory rest until ${dateStr}. No eligible pitchers available.`;
          }
        }
        pitcherGap = { reason: 'blocked', message };
        warnings.push(message);
      } else {
        const message = 'No eligible pitchers remain due to re-entry or rest rules.';
        pitcherGap = { reason: 'blocked', message };
        warnings.push(message);
      }
    }
    if (eligibleCandidates.length > 0) candidates = eligibleCandidates;

    if (fieldTimeOn && settings.noConsecutivePosition && inningLogs) {
      const nonConsecutive = candidates.filter(p => p.lastPosition !== pos);
      if (nonConsecutive.length > 0) candidates = nonConsecutive;
    }

    candidates.sort((a, b) => {
      const aRating = a.positions?.[pos] || 0;
      const bRating = b.positions?.[pos] || 0;
      if (aRating !== bRating) return bRating - aRating;
      return getEffectiveOVR(b) - getEffectiveOVR(a);
    });

    let selected = candidates[0];
    if (pos === 'P' && isPlayersPitch && eligibleCandidates.length === 0) selected = null;

    if (selected) {
      newLineup.push({ ...players.find(l => l.id === selected.id), position: pos });
      unassignedPlayers = unassignedPlayers.filter(p => p.id !== selected.id);
    } else if (pos !== 'P' || !isPlayersPitch) {
      selected =
        candidates[0] ??
        unassignedPlayers.find((p: any) => !lockedPitcherId || p.id !== lockedPitcherId) ??
        // Last resort: everyone left is the locked pitcher. Only take them when
        // the mound is already covered, so we never strip P to fill a fielder.
        (newLineup.some(n => n.position === 'P') ? unassignedPlayers[0] : undefined);
      if (selected) {
        newLineup.push({ ...players.find(l => l.id === selected.id), position: pos });
        unassignedPlayers = unassignedPlayers.filter(p => p.id !== selected.id);
      }
    }
  });

  // Leftover players (more present than field positions) become Extra Hitters or
  // bench, per the batting-order style:
  //   Continuous → all leftovers are EH (everyone bats)
  //   10 Players → exactly ONE leftover is the EH; the rest bench
  //   9 Players  → no EH; all leftovers bench
  const style = normalizeBattingOrderStyle(settings);
  let ehAllowed = style === 'Continuous' ? Infinity : style === '10 Players' ? 1 : 0;
  unassignedPlayers.forEach(p => {
    const pos = ehAllowed > 0 ? 'EH' : 'BENCH';
    if (ehAllowed > 0) ehAllowed--;
    newLineup.push({ ...players.find(l => l.id === p.id), position: pos });
  });

  let lineup = players.map(
    p => newLineup.find(n => n.id === p.id) || { ...p, position: 'BENCH' }
  );

  // ── Field Time Enforcement (hard rules) ──────────────────────────────────
  // Runs after position assignment, only when equalizer is on and at least
  // one inning has been logged in the current game.
  if (fieldTimeOn && inningLogs && Object.keys(inningLogs).length > 0) {

    // Rule 1: Minimum Fielding Innings Per Game
    if (settings.minFieldInningsEnabled && settings.minFieldInningsPerPlayer > 0) {
      const minInnings = settings.minFieldInningsPerPlayer;

      const underPlayed = lineup
        .filter(p => {
          if (p.position !== 'BENCH' && p.position !== 'EH') return false;
          return getInningsPlayed(p.id, inningLogs) < minInnings;
        })
        .sort((a, b) =>
          getInningsPlayed(a.id, inningLogs) - getInningsPlayed(b.id, inningLogs)
        );

      const overPlayed = lineup
        .filter(p => {
          if (p.position === 'BENCH' || p.position === 'EH' || (isPlayersPitch && p.position === 'P'))
            return false;
          return getInningsPlayed(p.id, inningLogs) >= minInnings;
        })
        .sort((a, b) =>
          getInningsPlayed(b.id, inningLogs) - getInningsPlayed(a.id, inningLogs)
        );

      const swapCount = Math.min(underPlayed.length, overPlayed.length);
      for (let i = 0; i < swapCount; i++) {
        const needsPlay  = underPlayed[i];
        const shouldSit  = overPlayed[i];
        if (!isEligible(needsPlay, shouldSit.position, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)) continue;
        lineup = lineup.map(p => {
          if (p.id === needsPlay.id)  return { ...p, position: shouldSit.position };
          if (p.id === shouldSit.id)  return { ...p, position: 'BENCH' };
          return p;
        });
      }

      if (underPlayed.length > overPlayed.length) {
        warnings.push(
          `⚠️ Field Time: ${underPlayed.length - overPlayed.length} player(s) haven't met the ${minInnings}-inning minimum but no eligible swap was available.`
        );
      }
    }

    // Rule 2: Equal Bench Innings
    if (settings.equalBenchInnings) {
      const benchCounts = lineup.map(p => ({
        id:    p.id,
        bench: getBenchInnings(p.id, inningLogs),
      }));
      const minBench = Math.min(...benchCounts.map(b => b.bench));
      const maxBench = Math.max(...benchCounts.map(b => b.bench));

      if (maxBench > minBench + 1) {
        const currentlyBenched   = lineup.filter(p => p.position === 'BENCH' || p.position === 'EH');
        const currentlyOnField   = lineup.filter(
          p => p.position !== 'BENCH' && p.position !== 'EH' && (!isPlayersPitch || p.position !== 'P')
        );

        // Don't bench a player who sat last inning when No Repeat Bench is on —
        // equalizing season totals must never override the no-two-in-a-row guard.
        const justSatLastInning = (id: string): boolean => {
          const last = getLastPosition(id, inningLogs);
          return last === 'BENCH' || last === 'EH';
        };

        const mostBenchedOnField = currentlyOnField
          .filter(p => !(settings.noConsecutiveBench && justSatLastInning(p.id)))
          .map(p => ({ ...p, benchCount: getBenchInnings(p.id, inningLogs) }))
          .sort((a, b) => b.benchCount - a.benchCount)[0];

        const leastBenchedOnBench = currentlyBenched
          .map(p => ({ ...p, benchCount: getBenchInnings(p.id, inningLogs) }))
          .sort((a, b) => a.benchCount - b.benchCount)[0];

        if (
          mostBenchedOnField &&
          leastBenchedOnBench &&
          mostBenchedOnField.id !== leastBenchedOnBench.id &&
          mostBenchedOnField.benchCount > leastBenchedOnBench.benchCount &&
          isEligible(leastBenchedOnBench, mostBenchedOnField.position, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
        ) {
          const vacated = mostBenchedOnField.position;
          const swapped = lineup.map(p => {
            if (p.id === leastBenchedOnBench.id) return { ...p, position: vacated };
            if (p.id === mostBenchedOnField.id)  return { ...p, position: 'BENCH' };
            return p;
          });
          // Only commit if the vacated spot is genuinely occupied afterwards. A
          // swap that empties a fielding position is worse than an uneven bench.
          if (swapped.some(p => p.position === vacated)) lineup = swapped;
        }
      }
    }

    // Rule 3: Must Play Infield Per Game
    if (settings.mustPlayInfieldEnabled && settings.mustPlayInfieldInnings > 0) {
      const INFIELD  = new Set(['P', 'C', '1B', '2B', 'SS', '3B']);
      const required = settings.mustPlayInfieldInnings;

      const getInfieldInnings = (playerId: string): number => {
        let count = 0;
        sortedInningKeys(inningLogs).forEach(k => {
          const entry = inningLogs[k].find((p: any) => p.id === playerId);
          if (entry && INFIELD.has(entry.position)) count++;
        });
        return count;
      };

      const needsInfield = lineup
        .filter(p => {
          if (isPlayersPitch && p.position === 'P') return false;
          return getInfieldInnings(p.id) < required && !INFIELD.has(p.position);
        })
        .sort((a, b) => getInfieldInnings(a.id) - getInfieldInnings(b.id));

      const infieldMet = lineup
        .filter(p => {
          if (!INFIELD.has(p.position) || (isPlayersPitch && p.position === 'P')) return false;
          return getInfieldInnings(p.id) >= required;
        })
        .sort((a, b) => getInfieldInnings(b.id) - getInfieldInnings(a.id));

      const swapCount = Math.min(needsInfield.length, infieldMet.length);
      for (let i = 0; i < swapCount; i++) {
        const movesToInfield  = needsInfield[i];
        const movesToOutfield = infieldMet[i];
        if (
          !isEligible(movesToInfield,  movesToOutfield.position, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay) ||
          !isEligible(movesToOutfield, movesToInfield.position,  settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
        ) continue;
        lineup = lineup.map(p => {
          if (p.id === movesToInfield.id)
            return { ...p, position: movesToOutfield.position };
          if (p.id === movesToOutfield.id)
            return { ...p, position: movesToInfield.position };
          return p;
        });
      }

      if (needsInfield.length > infieldMet.length) {
        warnings.push(
          `⚠️ Field Time: ${needsInfield.length - infieldMet.length} player(s) still need infield time but no eligible swap was available.`
        );
      }
    }

    // Rule 4: No Consecutive Position — warn only, no forced swap
    if (settings.noConsecutivePosition) {
      lineup.forEach(p => {
        if (p.position === 'BENCH' || p.position === 'EH' || (isPlayersPitch && p.position === 'P')) return;
        const lastPos = getLastPosition(p.id, inningLogs);
        if (lastPos && lastPos === p.position) {
          warnings.push(
            `⚠️ Field Time: ${p.name || p.id} is at ${p.position} again. Consider rotating manually.`
          );
        }
      });
    }
  }


  // Fill a fielding position that reconciliation just emptied. Prefers a rated,
  // eligible bench player; falls back to anyone sitting, because a body at the
  // spot always beats a hole in the defense. No-op for BENCH/EH or if the spot
  // is already covered.
  const backfillVacated = (lu: any[], pos: string, excludeIds: string[]): any[] => {
    if (!pos || pos === 'BENCH' || pos === 'EH') return lu;
    if (lu.some((p: any) => p.position === pos)) return lu;
    const sitting = lu.filter((p: any) =>
      (p.position === 'BENCH' || p.position === 'EH') && !excludeIds.includes(p.id));
    if (sitting.length === 0) return lu;
    const pick =
      [...sitting]
        .filter(p => isEligible(p, pos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay))
        .sort((a, b) => (b.positions?.[pos] || 0) - (a.positions?.[pos] || 0) ||
                        getEffectiveOVR(b) - getEffectiveOVR(a))[0]
      ?? [...sitting].sort((a, b) => getEffectiveOVR(b) - getEffectiveOVR(a))[0];
    return lu.map((p: any) => (p.id === pick.id ? { ...p, position: pos } : p));
  };

  // Pitcher placement reconciliation
  const lockedPitcher = isPlayersPitch && !settings.noPitcher
    ? lineup.find((p: any) => p.isCurrentPitcher)
    : null;

  if (lockedPitcher) {
    const engineAtP = lineup.find((p: any) => p.position === 'P');
    if (engineAtP && engineAtP.id !== lockedPitcher.id) {
      const vacatedPos = lockedPitcher.position;
      const engineAtPEligible =
        vacatedPos === 'BENCH' || vacatedPos === 'EH' ||
        isEligible(engineAtP, vacatedPos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay);

      if (!engineAtPEligible) {
        // engineAtP can't play the vacated position (e.g. lockedPitcher was at C
        // and engineAtP isn't a catcher). Find a 3-way swap: a fielder who CAN
        // play vacatedPos AND whose own position engineAtP can play.
        const bridge = lineup.find((p: any) =>
          p.id !== engineAtP.id &&
          p.id !== lockedPitcher.id &&
          p.position !== 'P' &&
          p.position !== 'BENCH' && p.position !== 'EH' &&
          isEligible(p, vacatedPos, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay) &&
          isEligible(engineAtP, p.position, settings, inningLogs, currentGameDate, currentTournamentId, styleOfPlay)
        );

        if (bridge) {
          const bridgePos = bridge.position;
          lineup = lineup.map((p: any) => {
            if (p.id === lockedPitcher.id) return { ...p, position: 'P' };
            if (p.id === bridge.id)        return { ...p, position: vacatedPos };
            if (p.id === engineAtP.id)     return { ...p, position: bridgePos };
            return p;
          });
        } else {
          // No 3-way swap available — bench engineAtP rather than place them
          // at a position they can't play, then backfill the spot the locked
          // pitcher just left so the field is never short a player.
          lineup = lineup.map((p: any) => {
            if (p.id === engineAtP.id)    return { ...p, position: 'BENCH' };
            if (p.id === lockedPitcher.id) return { ...p, position: 'P' };
            return p;
          });
          lineup = backfillVacated(lineup, vacatedPos, [lockedPitcher.id]);
        }
      } else {
        lineup = lineup.map((p: any) => {
          if (p.id === engineAtP.id)    return { ...p, position: vacatedPos };
          if (p.id === lockedPitcher.id) return { ...p, position: 'P' };
          return p;
        });
      }
    } else if (!engineAtP) {
      // Mound was empty and the locked pitcher was fielding elsewhere: moving
      // them to P vacates that spot, so backfill it from the bench.
      const vacatedPos = lockedPitcher.position;
      lineup = lineup.map((p: any) =>
        p.id === lockedPitcher.id ? { ...p, position: 'P' } : p
      );
      lineup = backfillVacated(lineup, vacatedPos, [lockedPitcher.id]);
    }
  } else if (!isPlayersPitch) {
    // If not Players Pitch, ensures P is treated like any fielder
  } else if (!settings.noPitcher) {
    // Players Pitch with no coach-selected starter yet.
    //
    // This used to bench whoever the engine had placed at P, which was doubly
    // wrong: it left the mound empty AND, because the best arms rate highest at
    // P, it benched the very pitchers the coach was most likely to want. With a
    // 9-player roster that meant a rated arm sat while a position went unfilled.
    //
    // The starting pitcher is still the coach's call, so we do not treat the
    // engine's pick as final: it stands as a suggestion and pitcherGap is set so
    // the Builder stops and asks for confirmation before the game starts.
    const suggested = lineup.find((p: any) => p.position === 'P');
    if (suggested) {
      pitcherGap = {
        reason: 'suggested',
        message: `Dugout suggests ${suggested.name || 'this player'} to start on the mound, but the call is yours.`,
      };
    } else if (!pitcherGap) {
      pitcherGap = {
        reason: 'unrated',
        message: 'No one on the roster is rated as a pitcher yet, so the mound is still open.',
      };
    }
  }

  // A coach-selected starter is honored even without a P rating, and the locked
  // pitcher block above may have placed them after the scarcity loop already
  // flagged a gap. Trust the finished lineup: if the coach's own pick is on the
  // mound there is nothing to ask about. A 'suggested' gap is deliberately kept,
  // because an engine guess still needs the coach to confirm it.

  if (pitcherGap && (pitcherGap as any).reason !== 'suggested' &&
      lineup.some((p: any) => p.position === 'P')) {
    pitcherGap = null;
  }

  return { lineup, warnings, pitcherGap };
};